import React, { useEffect, useRef } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import Button, { ButtonEnums } from '../Button';
import Icon from '../Icon';

const Notification = ({
  id,
  type = 'info',
  message,
  actions,
  onSubmit,
  onOutsideClick = () => {},
  onKeyPress,
}) => {
  const { t } = useTranslation(['Notification', 'MeasurementTable', 'Common']);
  const notificationRef = useRef(null);

  useEffect(() => {
    const notificationElement = notificationRef.current;
    const handleClick = function (event) {
      const isClickInside = notificationElement.contains(event.target);

      if (!isClickInside) {
        onOutsideClick();
      }
    };

    // Both a mouse down and up listeners are desired so as to avoid missing events
    // from elements that have pointer-events:none (e.g. the active viewport).
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('mouseup', handleClick);

    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('mouseup', handleClick);
    };
  }, [onOutsideClick]);

  useEffect(() => {
    notificationRef.current.focus();
  }, []);

  const iconsByType = {
    error: {
      icon: 'info',
      color: 'text-red-400',
    },
    warning: {
      icon: 'notificationwarning-diamond',
      color: 'text-amber-400',
    },
    info: {
      icon: 'notifications-info',
      color: 'text-cyan-400',
    },
    success: {
      icon: 'info',
      color: 'text-emerald-400',
    },
  };

  const getIconData = () => {
    return (
      iconsByType[type] || {
        icon: '',
        color: '',
      }
    );
  };

  const { icon, color } = getIconData();

  const translatedMessage = typeof message === 'string' ? t(message) : message;

  return (
    <div
      ref={notificationRef}
      className="pointer-events-auto mx-auto my-1 flex max-w-2xl w-[96%] md:w-auto flex-col sm:flex-row items-center justify-between gap-2.5 rounded-lg border border-slate-700/80 bg-[#161c28]/95 px-3.5 py-1.5 shadow-2xl backdrop-blur-md outline-none transition-all animate-fadeIn"
      data-cy={id}
      onKeyDown={onKeyPress}
      tabIndex={0}
    >
      <div className="flex min-w-0 items-center gap-2 self-start sm:self-center">
        <Icon
          name={icon}
          className={classnames('h-5 w-5 flex-shrink-0', color)}
        />
        <span className="text-xs md:text-[13px] font-medium text-slate-200">
          {translatedMessage}
        </span>
      </div>
      <div className="flex flex-shrink-0 flex-wrap items-center justify-end gap-1.5 self-end sm:self-center">
        {actions?.map((action, index) => {
          const actionText = typeof action.text === 'string' ? t(action.text) : action.text;
          return (
            <Button
              name={action.id}
              key={index}
              type={action.type}
              size={ButtonEnums.size.small}
              className="!h-7 !px-2.5 !py-0.5 !text-xs font-normal shadow-sm transition-colors"
              onClick={() => {
                onSubmit(action.value);
              }}
            >
              {actionText}
            </Button>
          );
        })}
      </div>
    </div>
  );
};

Notification.propTypes = {
  type: PropTypes.oneOf(['error', 'warning', 'info', 'success']),
  message: PropTypes.string.isRequired,
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string.isRequired,
      value: PropTypes.any.isRequired,
      type: PropTypes.oneOf([ButtonEnums.type.primary, ButtonEnums.type.secondary]).isRequired,
      size: PropTypes.oneOf([ButtonEnums.size.small, ButtonEnums.size.medium]),
    })
  ).isRequired,
  onSubmit: PropTypes.func.isRequired,
  /** Can be used as a callback to dismiss the notification for clicks that occur outside of it */
  onOutsideClick: PropTypes.func,
  onKeyPress: PropTypes.func,
};

export default Notification;
